import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  AUTH_STORES_STORAGE_KEY,
  fetchAccessibleStores,
  type AccessibleStore,
} from '../services/authService'
import { listStores, type StoreRecord } from '../services/storeService'
import { useAuth } from './AuthContext'

const CURRENT_STORE_KEY = 'shopora_store_admin_current_store_id'

export type CurrentStoreContextValue = {
  stores: AccessibleStore[]
  currentStoreId: number | null
  currentStore: AccessibleStore | null
  storeRecord: StoreRecord | null
  loading: boolean
  setCurrentStoreId: (storeId: number) => void
  /** Apply a store PATCH response to in-memory storeRecord when it matches the active store. */
  applyStoreRecord: (record: StoreRecord) => void
  refreshStores: () => Promise<void>
}

const CurrentStoreContext = createContext<CurrentStoreContextValue | null>(null)

function readStoredStores(): AccessibleStore[] {
  try {
    const raw = localStorage.getItem(AUTH_STORES_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as AccessibleStore[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function readStoredStoreId(): number | null {
  try {
    const raw = localStorage.getItem(CURRENT_STORE_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : null
  } catch {
    return null
  }
}

function legacyStoresFromApi(items: StoreRecord[]): AccessibleStore[] {
  return items.map((s) => ({
    storeId: s.id,
    name: s.name,
    subdomain: s.subdomain,
    organisationId: s.organisationId,
    role: 'store_owner' as const,
    modules: [],
    adminBlocked: false,
    billingRequired: false,
    subscription: null,
  }))
}

export function CurrentStoreProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [stores, setStores] = useState<AccessibleStore[]>(() => readStoredStores())
  const [currentStoreId, setCurrentStoreIdState] = useState<number | null>(() => readStoredStoreId())
  const [storeRecord, setStoreRecord] = useState<StoreRecord | null>(null)
  // Bumped by refreshStores() so the store record (settings, languages, …) is re-read after a save.
  const [storeRecordVersion, setStoreRecordVersion] = useState(0)
  const [loading, setLoading] = useState(true)

  const refreshStores = useCallback(async () => {
    if (!user) {
      setStores([])
      setCurrentStoreIdState(null)
      setStoreRecord(null)
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      let nextStores: AccessibleStore[] = []
      try {
        nextStores = await fetchAccessibleStores()
      } catch {
        nextStores = readStoredStores()
        if (nextStores.length === 0) {
          const { items } = await listStores()
          nextStores = legacyStoresFromApi(items)
          if (nextStores.length > 0) {
            localStorage.setItem(AUTH_STORES_STORAGE_KEY, JSON.stringify(nextStores))
          }
        }
      }

      setStoreRecordVersion((v) => v + 1)
      const visible = nextStores.filter((s) => !s.adminBlocked)
      setStores(visible.length > 0 ? visible : nextStores)

      const pool = visible.length > 0 ? visible : nextStores
      setCurrentStoreIdState((prev) => {
        const stored = readStoredStoreId()
        const candidate = stored ?? prev
        if (candidate != null && pool.some((s) => s.storeId === candidate)) {
          return candidate
        }
        const first = pool[0]?.storeId ?? null
        if (first != null) {
          localStorage.setItem(CURRENT_STORE_KEY, String(first))
        }
        return first
      })
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    void refreshStores()
  }, [refreshStores])

  useEffect(() => {
    if (currentStoreId == null) {
      setStoreRecord(null)
      return
    }
    let cancelled = false
    void listStores()
      .then(({ items }) => {
        if (cancelled) return
        const match = items.find((s) => s.id === currentStoreId) ?? null
        setStoreRecord(match)
      })
      .catch(() => {
        if (!cancelled) setStoreRecord(null)
      })
    return () => {
      cancelled = true
    }
  }, [currentStoreId, storeRecordVersion])

  const setCurrentStoreId = useCallback((storeId: number) => {
    setCurrentStoreIdState(storeId)
    localStorage.setItem(CURRENT_STORE_KEY, String(storeId))
  }, [])

  const applyStoreRecord = useCallback(
    (record: StoreRecord) => {
      if (currentStoreId === record.id) {
        setStoreRecord(record)
      }
    },
    [currentStoreId],
  )

  const currentStore = useMemo(
    () => stores.find((s) => s.storeId === currentStoreId) ?? null,
    [stores, currentStoreId],
  )

  const value = useMemo(
    () => ({
      stores,
      currentStoreId,
      currentStore,
      storeRecord,
      loading,
      setCurrentStoreId,
      applyStoreRecord,
      refreshStores,
    }),
    [stores, currentStoreId, currentStore, storeRecord, loading, setCurrentStoreId, applyStoreRecord, refreshStores],
  )

  return <CurrentStoreContext.Provider value={value}>{children}</CurrentStoreContext.Provider>
}

export function useCurrentStore() {
  const ctx = useContext(CurrentStoreContext)
  if (!ctx) {
    throw new Error('useCurrentStore must be used within CurrentStoreProvider')
  }
  return ctx
}
